import * as React from 'react';
import { useTranslation } from 'react-i18next';

import type { CopyIssue } from '@/lib/copyValidation';

/**
 * Localise a validation issue.
 *
 * `copyValidation` mirrors the Go validator and carries its English message as
 * a fallback, but the admin console itself ships in en/ar/eg-ar — an operator
 * working in Arabic must not get a bare English string. The issue `code` is the
 * translation key; `token` (the placeholder, term, or limit the rule is about)
 * is the interpolation.
 */
export function useCopyIssueText(): (issue: CopyIssue) => string {
  const { t } = useTranslation('copy');
  return React.useCallback(
    (issue: CopyIssue) =>
      t(`issues.${issue.code}`, {
        defaultValue: issue.message,
        token: issue.token ?? '',
        field: issue.field,
      }),
    [t]
  );
}

/** Collapse a validation result to `{ leafPath: message }` for the editors. */
export function fieldErrorMap(
  issues: readonly CopyIssue[],
  render: (issue: CopyIssue) => string
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const issue of issues) {
    out[issue.field] ??= render(issue);
  }
  return out;
}
